import { Button, Col, Row, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ANCHOR_KIND,
  anchorFromDraft,
  anchorToDraft,
  presetByKey,
  validateAnchorPair
} from '../anchors';
import { resolveTapTargetBounds, validateTapTarget } from '../constants';
import {
  findTrackConflict,
  orderingError,
  resolveAnchorLocally,
  slotBounds
} from '../timeline';
import { toDatetimeLocal } from '../waveEditPayload';
import { buildCreatePayload } from '../waveAnchorPayload';
import AnchorScheduleFields from './AnchorScheduleFields';
import RewardsFields, {
  normalizeRewards,
  toEditableRewards,
  validateRewards
} from './RewardsFields';
import { FieldError, Notice, fmtDateTime, fmtTime } from './timelineUi';

// ADD A WAVE — the create path, expressed in exactly the same two pickers the edit rows use.
//
// The default open is deliberate rather than convenient: it is the moment the previous wave
// CLOSES. That makes the common case ("another wave after this one") a single click, and because
// the overlap comparison is half-open — one wave may close at 20:30 and the next open at 20:30 —
// adjacency is guaranteed to produce neither an overlap nor a gap. With nothing to hand over from,
// it falls back to preset A rather than inventing a time.

const MIN_MS = 60 * 1000;

const NewWaveEditor = ({ teamId, gameId, timeline, config, seedOpenDraft, onCancel, onCreated }) => {
  const [openDraft, setOpenDraft] = useState(() =>
    seedOpenDraft ||
    anchorToDraft(presetByKey('A').seed({ postFtMinutes: timeline.postFtMinutes }).open)
  );
  const [closeDraft, setCloseDraft] = useState(() => {
    if (seedOpenDraft?.kind === ANCHOR_KIND.ABSOLUTE && seedOpenDraft.at) {
      // Handing over from the previous close: a 30-minute window is a starting point the admin
      // then drags or retypes, not a claim about what the wave should be.
      const at = new Date(new Date(seedOpenDraft.at).getTime() + 30 * MIN_MS);
      return anchorToDraft({ kind: ANCHOR_KIND.ABSOLUTE, at }, toDatetimeLocal);
    }
    return anchorToDraft(presetByKey('A').seed({ postFtMinutes: timeline.postFtMinutes }).close);
  });
  const [target, setTarget] = useState('');
  const [rewards, setRewards] = useState(() => toEditableRewards(config?.rewards));
  const [presetKey, setPresetKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  // Rewards start from the channel defaults, which only arrive once /config has loaded.
  useEffect(() => {
    if (config?.rewards) setRewards(toEditableRewards(config.rewards));
  }, [config]);

  const bounds = useMemo(() => resolveTapTargetBounds(config), [config]);
  const effectiveTarget = target === '' ? config?.tapTarget : Number(target);

  const validation = validateAnchorPair(openDraft, closeDraft);
  const orderErr = validation.pair
    ? null
    : orderingError(anchorFromDraft(openDraft), anchorFromDraft(closeDraft), {
        phaseByEvent: timeline.phaseByEvent,
        postFtMinutes: timeline.postFtMinutes
      });

  // The projected span, and whether it lands on a wave already on the track. The API refuses an
  // overlap with a 400 naming the other wave; catching it here means the admin never gets there.
  const openResolved = resolveAnchorLocally(anchorFromDraft(openDraft), {
    phaseByEvent: timeline.phaseByEvent,
    postFtMinutes: timeline.postFtMinutes
  });
  const closeResolved = resolveAnchorLocally(anchorFromDraft(closeDraft), {
    phaseByEvent: timeline.phaseByEvent,
    postFtMinutes: timeline.postFtMinutes,
    openResolved
  });
  const clash =
    openResolved && closeResolved
      ? findTrackConflict({ from: openResolved.at, to: closeResolved.at }, timeline.waves)
      : null;

  // The free window this wave is being placed in, as `min`/`max` for the date-time fields. This is
  // the primary defence against an overlap: the times another wave already holds are not offerable,
  // so the admin cannot pick one. `clash` above stays as the backstop, because an event or
  // match-minute edge resolves to a projection that no input bound can express.
  const slots = slotBounds(timeline, openResolved?.at, { toLocal: toDatetimeLocal });

  const targetErr = target === '' ? null : validateTapTarget(target, bounds);
  const rewardsErr = validateRewards(rewards, effectiveTarget);

  const blockingError =
    validation.open || validation.close || validation.pair || orderErr || targetErr || rewardsErr;

  const canSave = !blockingError && !clash && !saving && !!gameId;

  const create = async () => {
    if (!canSave) return;
    setSaving(true);
    setServerError(null);
    const payload = buildCreatePayload({
      teamId,
      gameId,
      openDraft,
      closeDraft,
      target,
      rewards: normalizeRewards(rewards),
      baseRewards: config?.rewards
    });
    const res = await Api.post('/admin/fanwave/create', payload, null, { silent: true });
    setSaving(false);
    if (!res || res.err) {
      setServerError(res?.err || 'Could not create this wave. Please try again.');
      return;
    }
    onCreated && onCreated(res);
  };

  return (
    <Col
      noFlex
      wid="100%"
      gap="13px"
      pad="15px 16px"
      style={{
        borderRadius: 12,
        border: `1px solid ${theme.SECONDARY}`,
        background: '#fff'
      }}
    >
      <Row center between noFlex wid="100%" gap="10px">
        <Text b2 bold marg="0" style={{ fontSize: 13.5 }}>
          New wave
        </Text>
        {openResolved && closeResolved && (
          <Text c1 col={theme.GREY_60} marg="0" style={{ fontSize: 11.5 }}>
            {`${openResolved.observed ? '' : '~'}${fmtTime(openResolved.at)} → ${
              closeResolved.observed ? '' : '~'
            }${fmtTime(closeResolved.at)}`}
          </Text>
        )}
      </Row>

      <AnchorScheduleFields
        openDraft={openDraft}
        closeDraft={closeDraft}
        onOpenChange={setOpenDraft}
        onCloseChange={setCloseDraft}
        presetKey={presetKey}
        onPresetKeyChange={setPresetKey}
        postFtMinutes={timeline.postFtMinutes}
        phaseByEvent={timeline.phaseByEvent}
        openResolved={openResolved}
        validation={validation}
        orderError={orderErr}
        bounds={slots}
      />

      {slots?.tooNarrow && (
        <Notice tone="warning" title="This gap is under a minute wide">
          The free window here is shorter than the one-minute precision a date &amp; time field
          holds, so it cannot be filled. Move an edge of a neighbouring wave to open up room.
        </Notice>
      )}

      {clash && (
        <Notice tone="warning" title="This window overlaps a wave already on the track">
          {`It runs into the wave that opens ${fmtDateTime(clash.opensAt)} and closes ${fmtDateTime(
            clash.closesAt
          )}. Only one wave holds the session, so the API refuses an overlap — move an edge, or open exactly where that wave closes (touching at a boundary is fine).`}
        </Notice>
      )}

      <RewardsFields
        rewards={rewards}
        onChange={setRewards}
        target={target}
        effectiveTarget={effectiveTarget}
        onTargetChange={setTarget}
        targetError={targetErr}
        targetDescription={`Leave blank to inherit the channel default${
          config?.tapTarget ? ` (${config.tapTarget})` : ''
        }.`}
        numbered={false}
      />

      {rewardsErr && <FieldError>{rewardsErr}</FieldError>}
      {serverError && (
        <Notice tone="danger" title="Could not create">
          {serverError}
        </Notice>
      )}

      <Row center noFlex wid="100%" gap="9px" style={{ flexWrap: 'wrap' }}>
        <Button
          text={saving ? 'Creating…' : 'Create wave'}
          onClick={create}
          disabled={!canSave}
          bg={theme.SECONDARY}
          small
          wid="140px"
        />
        <Button text="Discard" onClick={onCancel} bordered small wid="110px" />
      </Row>
    </Col>
  );
};

export default NewWaveEditor;
